/**
 * app.store.ts — global app settings store
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { SupportedLang } from '@properwallet/i18n'
import { getSavedLang, setLang } from '@properwallet/i18n'
import { DEFAULT_EXPLORER_ID, EOS_RPC_ENDPOINTS } from '@properwallet/chain-eos'
import { setPreferredResolveEndpoints } from '@properwallet/wallet-core'

function syncResolveEndpoints(rpcUrl: string) {
  setPreferredResolveEndpoints([rpcUrl, ...EOS_RPC_ENDPOINTS])
}

interface AppStore {
  lang: SupportedLang
  rpcUrl: string
  favoriteDApps: string[]
  hideSmallBalances: boolean
  /**
   * Unverified tokens the user explicitly pulled out of the hidden section,
   * keyed by `${contract}:${symbol}`. Everything unverified is hidden until it
   * appears here — an airdropped scam token should never greet you on top.
   */
  manuallyShownTokens: string[]
  /** Tokens the user manually hid from the Wallet screen, keyed by `${contract}:${symbol}`. */
  manuallyHiddenTokens: string[]
  showNfts: boolean
  /** Selected block explorer id (see BLOCK_EXPLORERS in chain-eos config) */
  selectedExplorer: string
  /** 'fresh' = first launch; 'add-account' = import from account switcher */
  onboardingMode: 'fresh' | 'add-account'
  /** Unread mechat messages across all conversations. Transient — never persisted. */
  mechatUnread: number
  /** Unread count per peer account, for the conversation list. */
  mechatUnreadByPeer: Record<string, number>
  /**
   * Local address book: EOS account -> human name ("Паша").
   * Device-local, never published — an EOS account name is unreadable by design,
   * so the wallet remembers who is who.
   */
  contactNames: Record<string, string>
  /** URL to open in the in-app browser when Discovery mounts. */
  discoveryUrl: string | null
  /**
   * Accounts whose messages are hidden everywhere in the app. Messages cannot be
   * deleted from the chain, so blocking is client-side by definition.
   */
  blockedAccounts: string[]
  /**
   * Conversations hidden from the chat list on this device. Unlike blocking, the
   * peer can still send messages — they just stay out of sight until unhidden.
   */
  hiddenChatPeers: string[]
  /**
   * Peers whose attachments the user chose to reveal even though they've never
   * written to them. Persisted so the choice sticks across restarts (per device —
   * another device asks again).
   */
  revealedChatPeers: string[]
  /** The chat terms have been accepted — required before the first message. */
  mechatTosAccepted: boolean

  setLanguage: (lang: SupportedLang) => void
  setRpcUrl: (url: string) => void
  setSelectedExplorer: (id: string) => void
  toggleFavoriteDApp: (id: string) => void
  setHideSmallBalances: (v: boolean) => void
  showToken: (key: string) => void
  unshowToken: (key: string) => void
  hideToken: (key: string) => void
  unhideToken: (key: string) => void
  setShowNfts: (v: boolean) => void
  setOnboardingMode: (mode: 'fresh' | 'add-account') => void
  setMechatUnread: (total: number, byPeer: Record<string, number>) => void
  /** Pass an empty name to forget the alias. */
  setContactName: (account: string, name: string) => void
  setDiscoveryUrl: (url: string | null) => void
  toggleBlockedAccount: (account: string) => void
  isBlocked: (account: string) => boolean
  hideChatPeer: (account: string) => void
  unhideChatPeer: (account: string) => void
  isChatHidden: (account: string) => boolean
  revealChatPeer: (account: string) => void
  isChatPeerRevealed: (account: string) => boolean
  acceptMechatTos: () => void
}

export const useAppStore = create<AppStore>()(
  persist(
    (set, get) => ({
      lang: getSavedLang(),
      rpcUrl: EOS_RPC_ENDPOINTS[0],
      favoriteDApps: ['webdex', 'vaulta-explorer', 'malinkaharvest'],
      hideSmallBalances: false,
      manuallyShownTokens: [],
      manuallyHiddenTokens: [],
      showNfts: true,
      selectedExplorer: DEFAULT_EXPLORER_ID,
      onboardingMode: 'fresh',
      mechatUnread: 0,
      mechatUnreadByPeer: {},
      contactNames: {},
      discoveryUrl: null,
      blockedAccounts: [],
      hiddenChatPeers: [],
      revealedChatPeers: [],
      mechatTosAccepted: false,

      setLanguage: (lang) => {
        setLang(lang)
        set({ lang })
      },

      setRpcUrl: (rpcUrl) => {
        syncResolveEndpoints(rpcUrl)
        set({ rpcUrl })
      },

      setSelectedExplorer: (selectedExplorer) => set({ selectedExplorer }),

      toggleFavoriteDApp: (id) => {
        const favs = get().favoriteDApps
        const exists = favs.includes(id)
        set({
          favoriteDApps: exists ? favs.filter(f => f !== id) : [...favs, id],
        })
      },

      setHideSmallBalances: (v) => set({ hideSmallBalances: v }),
      showToken: (key) => {
        const cur = get().manuallyShownTokens
        if (cur.includes(key)) return
        set({ manuallyShownTokens: [...cur, key] })
      },
      unshowToken: (key) => {
        set({ manuallyShownTokens: get().manuallyShownTokens.filter(k => k !== key) })
      },
      hideToken: (key) => {
        const cur = get().manuallyHiddenTokens
        if (cur.includes(key)) return
        set({ manuallyHiddenTokens: [...cur, key] })
      },
      unhideToken: (key) => {
        set({ manuallyHiddenTokens: get().manuallyHiddenTokens.filter(k => k !== key) })
      },
      setShowNfts: (v) => set({ showNfts: v }),
      setOnboardingMode: (onboardingMode) => set({ onboardingMode }),
      setMechatUnread: (mechatUnread, mechatUnreadByPeer) => set({ mechatUnread, mechatUnreadByPeer }),
      setContactName: (account, name) => {
        const next = { ...get().contactNames }
        const trimmed = name.trim()
        if (trimmed) next[account] = trimmed.slice(0, 40)
        else delete next[account]
        set({ contactNames: next })
      },
      setDiscoveryUrl: (discoveryUrl) => set({ discoveryUrl }),
      toggleBlockedAccount: (account) => {
        const cur = get().blockedAccounts
        set({
          blockedAccounts: cur.includes(account)
            ? cur.filter(a => a !== account)
            : [...cur, account],
        })
      },
      isBlocked: (account) => get().blockedAccounts.includes(account),
      hideChatPeer: (account) => {
        const cur = get().hiddenChatPeers
        if (cur.includes(account)) return
        set({ hiddenChatPeers: [...cur, account] })
      },
      unhideChatPeer: (account) => {
        set({ hiddenChatPeers: get().hiddenChatPeers.filter(a => a !== account) })
      },
      isChatHidden: (account) => get().hiddenChatPeers.includes(account),
      revealChatPeer: (account) => {
        const cur = get().revealedChatPeers
        if (cur.includes(account)) return
        set({ revealedChatPeers: [...cur, account] })
      },
      isChatPeerRevealed: (account) => get().revealedChatPeers.includes(account),
      acceptMechatTos: () => set({ mechatTosAccepted: true }),
    }),
    {
      name: 'properwallet-app-settings',
      // v2: unverified tokens are hidden by default; drop the old always-show flag.
      version: 2,
      migrate: (persisted) => {
        const state = (persisted ?? {}) as Record<string, unknown>
        delete state.showUnknownTokens
        if (!Array.isArray(state.manuallyShownTokens)) state.manuallyShownTokens = []
        return state as never
      },
      partialize: (s) => ({
        lang: s.lang,
        rpcUrl: s.rpcUrl,
        favoriteDApps: s.favoriteDApps,
        hideSmallBalances: s.hideSmallBalances,
        manuallyShownTokens: s.manuallyShownTokens,
        manuallyHiddenTokens: s.manuallyHiddenTokens,
        showNfts: s.showNfts,
        selectedExplorer: s.selectedExplorer,
        contactNames: s.contactNames,
        blockedAccounts: s.blockedAccounts,
        hiddenChatPeers: s.hiddenChatPeers,
        revealedChatPeers: s.revealedChatPeers,
        mechatTosAccepted: s.mechatTosAccepted,
      }),
      onRehydrateStorage: () => (state) => {
        if (state?.rpcUrl) syncResolveEndpoints(state.rpcUrl)
        else syncResolveEndpoints(EOS_RPC_ENDPOINTS[0])
      },
    },
  ),
)

// Initial sync before rehydrate completes (defaults)
syncResolveEndpoints(EOS_RPC_ENDPOINTS[0])
